import { PublicLinkPageStatus } from '@confluence/public-links/entry-points/PublicLinkPageQuery';
import { expValEquals } from '@confluence/feature-experiments';

import type { CtaNotifyMessageFooterProps } from '../controlled/shareAndRestrictDialog/footer/CtaNotifyMessageFooter';
import type { DistributionFooterProps } from '../controlled/shareAndRestrictDialog/footer/DistributionFooter';
import type { FooterProps } from '../controlled/shareAndRestrictDialog/footer/Footer';
import type { MessageComposerProps } from '../controlled/shareAndRestrictDialog/footer/MessageComposer';

import type { StatesAndActions } from './Controller';
import { makePrincipalsMap } from './principalsMap';

export function makeFooterProps(statesAndActions: StatesAndActions): FooterProps {
	const {
		derivedState: { natureOfCta, isPublishModalFlow },
	} = statesAndActions;
	return natureOfCta === 'none'
		? {
				ctaNotifyMessageFooter: undefined,
				distributionFooter: isPublishModalFlow
					? undefined
					: makeDistributionFooterProps(statesAndActions),
			}
		: {
				ctaNotifyMessageFooter: makeCtaNotifyMessageFooterProps(statesAndActions),
				distributionFooter: isPublishModalFlow
					? undefined
					: makeDistributionFooterProps(statesAndActions),
			};
}

function isPublicLinkEditAllowedForContentType(statesAndActions: StatesAndActions): boolean {
	const { publicLinkPageData } = statesAndActions.queriesState;
	return publicLinkPageData.isLoading
		? false
		: publicLinkPageData.isPublicLinkEditAllowedForContentType;
}

function isPublicLinkOn(statesAndActions: StatesAndActions): boolean {
	const { publicLinkPageData } = statesAndActions.queriesState;
	if (
		!publicLinkPageData.isLoading &&
		publicLinkPageData.publicLinkStatus === PublicLinkPageStatus.ON
	) {
		return true;
	}
	return false;
}

function makeDistributionFooterProps(statesAndActions: StatesAndActions): DistributionFooterProps {
	const {
		actions: { openPublicLinkDialog, openShareToSlackDialog, clearElementToFocus },
		controllerProps: { contentId, contentType, contentSubType, pageMode, isUnpublishedDraft },
		queriesState: { spaceKey },
		derivedState: { isLockedOut, natureOfCta },
		controllerExtras: { publicLinkButtonRef, shareToSlackButtonRef },
	} = statesAndActions;

	return {
		contentId,
		contentType,
		spaceKey,
		pageMode,
		isLockedOut,
		isUnpublishedDraft,
		isLivePage: contentSubType === 'live',
		onSlackClick: openShareToSlackDialog,
		onPublicLinkClick: openPublicLinkDialog,
		isPublicLinkOn: isPublicLinkOn(statesAndActions),
		isPublicLinkEditAllowedForContentType: isPublicLinkEditAllowedForContentType(statesAndActions),
		lastTriggeredDialogVariant: statesAndActions.masterState.lastTriggeredDialogVariant,
		clearElementToFocus,
		publicLinkButtonRef,
		shareToSlackButtonRef,
		natureOfCta,
	};
}

function makeMessageComposerProps(
	statesAndActions: StatesAndActions,
	rows: number,
	notifyCheckbox: 'hidden' | 'checked' | 'unchecked',
): MessageComposerProps {
	const {
		derivedState: { isLockedOut },
		masterState: { optionalMessage },
		actions: { changeOptionalMessage },
	} = statesAndActions;

	return {
		isLockedOut,
		notifyMessage: optionalMessage,
		rows,
		isCheckboxUnchecked: notifyCheckbox === 'unchecked',
		onNotifyMessageChange: changeOptionalMessage,
	};
}

export function makeCtaNotifyMessageFooterProps(
	statesAndActions: StatesAndActions,
): CtaNotifyMessageFooterProps {
	const {
		masterState: {
			isSaving,
			notifyCheckedIfAvailable,
			showMessageComposerUnderCheckbox,
			isSpecificAccessExpanded,
			principalsStagerPrincipals,
		},
		derivedState: {
			isLockedOut,
			natureOfCta,
			isCheckboxVisible,
			areEmailsOnlyPresent,
			sharingType,
			specificAccessIds,
			exceededLimit,
			isSharingToEmailsDisabled,
			includeInvites,
			stagedGeneralAccessLevel,
		},
		actions,
		controllerProps: { pageMode, isUnpublishedDraft, contentSubType, contentId },
	} = statesAndActions;

	const notifyCheckbox = (() => {
		if (!isCheckboxVisible) return 'hidden';
		return notifyCheckedIfAvailable ? 'checked' : 'unchecked';
	})();

	const shouldShowMessageComposer =
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') ||
		(notifyCheckbox === 'checked' && showMessageComposerUnderCheckbox);

	const checkboxLabel = (() => {
		if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
			return 'notify-them';
		}
		if (areEmailsOnlyPresent) return 'recipients';
		return 'newly-added';
	})();

	const isSpecificAccessDetailsVisible = specificAccessIds.length > 0 && isSpecificAccessExpanded;

	const hasGroups = principalsStagerPrincipals.some((p) => p.type === 'group');
	const hasTeams = principalsStagerPrincipals.some((p) => p.type === 'team');
	const hasUsers = principalsStagerPrincipals.some(
		(p) => p.type && ['user', 'guest', 'email'].includes(p.type),
	);

	const principalsCount = makePrincipalsMap(
		statesAndActions.masterState,
		statesAndActions.queriesState,
		{
			isCtaClick: true,
			stagedGeneralAccessLevel,
		},
	).size;

	const messageComposerRows = expValEquals(
		'persistence_specific_access_gate',
		'cohort',
		'experiment',
	)
		? 5
		: 3;

	return {
		natureOfCta,
		notifyCheckbox,
		checkboxLabel,
		isLockedOut,
		isCtaInProgress: isSaving,
		messageComposer: shouldShowMessageComposer
			? makeMessageComposerProps(statesAndActions, messageComposerRows, notifyCheckbox)
			: undefined,
		sharingType,
		onCtaClick: actions.handleCtaClick,
		onCancelClick: actions.dismiss,
		isSpecificAccessDetailsVisible,
		onNotifyCheckboxClick: () => {
			actions.setNotifyChecked(!notifyCheckedIfAvailable);
		},
		onNotifyAddMessageClick: actions.toggleNotifyAddMessage,
		isLimitExceeded: exceededLimit.isLimitExceeded,
		pageMode,
		isDraft: isUnpublishedDraft,
		isLivePage: contentSubType === 'live',
		isSharingToEmailsDisabled,
		contentId,
		hasGroups,
		hasTeams,
		hasUsers,
		includeInvites,
		generalAccessLevel: stagedGeneralAccessLevel,
		principalsCount,
	};
}
